import {
    Box,
    FormControl,
    FormGroup,
    ListItemText,
    MenuItem
} from '@mui/material';
import i18n from '../../i18';
import React, { useEffect, useMemo } from 'react';
import { useBasicFilters } from './hooks/useBasicFilters';
import Scrollbar from '../../components/scrollbar/Scrollbar';
import CustomCalendar from '../../components/calendar/CustomCalendar';
import moment from 'moment';
import { formatFilterData } from '../../utils/formatCalendar';
import CheckboxCustom from '../../components/checkbox';

export const HistoryFilter = ({
    initFilters,
    onFiltersChange,
    head,
    historyFieldLabels,
    loading,
    ...props
}) => {
    const { parentLabel, statusLabel, dateFromLabel, dateToLabel } =
        historyFieldLabels;
    const createFilterFieldLabel = (fieldLabel, parentFieldLabel) => {
        return parentFieldLabel
            ? `${parentFieldLabel}.${fieldLabel}`
            : fieldLabel;
    };
    const dateFromField = createFilterFieldLabel(dateFromLabel, parentLabel);
    const dateToField = createFilterFieldLabel(dateToLabel, parentLabel);
    const statusField = createFilterFieldLabel(statusLabel, parentLabel);

    const { filters, replaceFilters, addFilter, updateFilter, removeFilter } =
        useBasicFilters(initFilters);

    useEffect(() => {
        if (JSON.stringify(filters) !== JSON.stringify(initFilters)) {
            replaceFilters(initFilters);
        }
    }, [initFilters]);

    useEffect(() => {
        onFiltersChange(filters);
    }, [filters]);

    const [start, end] = useMemo(() => {
        const startValue = filters.find((f) => f.op === '>')?.value;
        const endValue = filters.find((f) => f.op === '<')?.value;
        return [startValue, endValue];
    }, [filters]);

    const handleChangeRange = (rangeFilter) => {
        replaceFilters((prev) => {
            const newFilters = prev.filter((f) => {
                return (
                    f && f.field !== dateFromField && f.field !== dateToField
                );
            });
            return [...newFilters, ...rangeFilter];
        });
    };

    const handleChangeCalendar = (value) => {
        const filterData = formatFilterData(
            value.startDate,
            value.endDate,
            dateFromField,
            dateToField
        );
        return handleChangeRange(filterData);
    };

    const handleStatusChange = (status) => {
        const existingStatusFilter = filters.find(
            (f) => f.field === statusField
        );
        if (existingStatusFilter) {
            return status === null
                ? removeFilter(statusField, '=')
                : updateFilter(statusField, '=', status);
        }
        return status === null
            ? undefined
            : addFilter(statusField, '=', status);
    };

    const statusOptions = (() => {
        const statusHeadField = head.find((field) => field.id === statusLabel);
        if (statusHeadField && statusHeadField.items) {
            return statusHeadField.items.map((name) => ({
                value: name,
                label: i18n.t(`enums.${name}`)
            }));
        }
        return [];
    })();

    const isSelected = (status) => {
        const existingStatusFilter = filters.find(
            (f) => f.field === statusField
        );
        return existingStatusFilter?.value === status;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Scrollbar sx={{ maxHeight: 500, mt: 1 }}>
                <MenuItem>{i18n.t('columns.status')}</MenuItem>
                <FormControl>
                    <FormGroup>
                        {statusOptions.map((option) => {
                            const optionKey = option.value;
                            const optionName = option.label;
                            return (
                                <MenuItem
                                    key={optionKey}
                                    value={optionKey}
                                    disabled={loading}
                                    selected={isSelected(optionKey)}
                                    onClick={() =>
                                        handleStatusChange(optionKey)
                                    }
                                    // style={style}
                                    sx={{
                                        '&.Mui-selected': {
                                            backgroundColor: (theme) =>
                                                theme.palette.action
                                                    .mainSelected
                                        }
                                    }}
                                >
                                    <CheckboxCustom
                                        checked={isSelected(optionKey)}
                                    />
                                    <ListItemText
                                        primary={optionName}
                                        sx={{ ml: 1 }}
                                        primaryTypographyProps={{
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden'
                                        }}
                                    />
                                </MenuItem>
                            );
                        })}
                        <MenuItem>{i18n.t('columns.date')}</MenuItem>
                        <CustomCalendar
                            value={{
                                startDate: moment(start),
                                endDate: moment(end)
                            }}
                            disabled={loading}
                            onChange={(value) => handleChangeCalendar(value)}
                            positionShortcut="top"
                            shortcuts={[
                                {
                                    label: 'today',
                                    days: 1
                                },
                                {
                                    label: 'thisWeek',
                                    days: 7
                                },
                                {
                                    label: 'thisMonth',
                                    days: 30
                                }
                            ]}
                        />
                    </FormGroup>
                </FormControl>
            </Scrollbar>
        </Box>
    );
};
